import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { webApi } from '@mi-front/api-clients';
import { Box, Card, Divider, Stack } from '@mui/material';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FRAGMENT_LIST_CARD_MEDIA_WIDTH } from '../constants';
import { FragmentCardCreator } from './FragmentCardCreator';
import { FragmentCardRelativeTime } from './FragmentCardRelativeTime';
import { FragmentCardStatuses } from './FragmentCardStatuses';
import { FragmentCardTags } from './FragmentCardTags';
import { FragmentCardTime } from './FragmentCardTime';
import { FragmentCardTitle } from './FragmentCardTitle';
import { fragmentCardListSx } from './styles';

/**
 * List layout base for fragment cards. Renders media (caller-provided), then
 * title, time/duration, creator, statuses (shared for audio and video).
 * - Part 1: Media (waveform/poster)
 * - Part 2: Title + time/duration
 * - Divider
 * - Part 3: Creator + created at
 * - Divider
 * - Part 4: Statuses
 */
export function FragmentCardListBase({
  fragment,
  media,
}: {
  fragment: webApi.FragmentListItemDto;
  /** Media area: video poster or audio waveform. */
  media: ReactNode;
}) {
  const to = routeGenerator.view(fragment.id);

  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <Card sx={fragmentCardListSx}>
        {/* Part 1: Media — rounded left edge and overflow hidden like MUI CardMedia */}
        <Box
          sx={{
            width: FRAGMENT_LIST_CARD_MEDIA_WIDTH,
            minWidth: FRAGMENT_LIST_CARD_MEDIA_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            borderTopLeftRadius: (theme) => theme.shape.borderRadius,
            borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
            borderTopRightRadius: (theme) => theme.shape.borderRadius,
            borderBottomRightRadius: (theme) => theme.shape.borderRadius,
          }}
        >
          {media}
        </Box>

        {/* Part 2: Title + Time/Duration */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            pl: 1,
          }}
        >
          <FragmentCardTitle
            title={fragment.title}
            sx={{
              lineHeight: 1.6,
              height: '3.2em',
            }}
          />
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              typography: 'body2',
              color: 'text.secondary',
            }}
          >
            <FragmentCardTime from={fragment.from} showRecentDate dateFormat="long" />
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Part 3: Creator + Created At */}
        <Box
          sx={{
            width: 300,
            minWidth: 250,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FragmentCardCreator creator={fragment.creator} />
              <FragmentCardRelativeTime createdAt={fragment.createdAt} />
            </Box>
            {!!fragment.tags?.length && (
              <Box>
                <FragmentCardTags tags={fragment.tags as unknown as string[]} maxVisible={3} />
              </Box>
            )}
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Part 4: Statuses */}
        <Box
          sx={{
            width: 140,
            minWidth: 120,
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FragmentCardStatuses
            uploadStatuses={fragment.uploadStatuses}
            externalDelivery={fragment.externalDelivery}
            publishedPlatforms={fragment.publishedPlatforms}
            externalDeliveryStartedAt={fragment.externalDeliveryStartedAt}
            contentAiCreatedAt={fragment.contentAiCreatedAt}
            size="medium"
            maxVisibleItems={10}
            showUnpublished
          />
        </Box>
      </Card>
    </Link>
  );
}
